import { Icon } from '../../components/ui/Icon'
import type { PartAttachmentsService } from '../parts/attachments/usePartAttachments'
import { defaultPartAttachmentsService } from '../parts/attachments/usePartAttachments'
import type { RfqLineFileView } from './domain'

/**
 * UID-528 follow-up — the per-RFQ-line file chip, EXTRACTED VERBATIM out of
 * `RfqCard.tsx` so the compose/draft screen's parts table
 * (`PartsReviewTable.tsx`) renders the SAME chip rather than a second,
 * silently-diverging copy of the same JSX.
 *
 * This is a pure move: `openRfqLineFile`, `drawingFilesOnly`,
 * `RfqLineFileChip` and `FileModelGlyph` are unchanged in behavior, markup and
 * class names — only their home changed. The two surfaces that render a line's
 * drawing (the submitted-request DETAIL page and the draft COMPOSE page) must
 * look and behave identically per the design's shared "Name & drawing" column,
 * and one component is the only way to keep that true.
 *
 * The chip is deliberately data-source-agnostic: it takes a `partId` + an
 * `RfqLineFileView` and knows nothing about whether those were resolved
 * SERVER-side (a persisted RFQ's `RfqLineView.files`, `line-file-read.ts`) or
 * CLIENT-side (a draft's `useDraftLineDrawings`). Both resolve to the same
 * `(partId, attachmentId)` pair, which is all the download seam needs.
 */

/**
 * UID-528 — resolves the SAME presigned-GET download URL the durable Part
 * Attachments UI uses (`usePartAttachments.downloadAttachment`'s exact
 * `defaultPartAttachmentsService.getDownloadUrl` + `window.open(url,
 * '_blank', 'noopener,noreferrer')` posture), rather than a second fetch
 * wrapper. Deliberately NOT that hook itself: `usePartAttachments` fetches
 * the full attachment list on mount and refetches on window focus — the
 * wrong shape for a per-row chip in a table of RFQ lines, which only ever
 * needs a single on-demand GET keyed by `(partId, attachmentId)` already in
 * hand at the call site. `getDownloadUrl` is an injectable param (defaults
 * to the real service) purely so tests can supply a fake without a second
 * network-mocking convention. A failed resolve reports through `onError`
 * (the caller's existing `showToast` convention) — this never throws an
 * unhandled rejection or crashes the table.
 */
export async function openRfqLineFile(
  partId: string,
  attachmentId: string,
  onError: (message: string) => void,
  getDownloadUrl: PartAttachmentsService['getDownloadUrl'] = defaultPartAttachmentsService.getDownloadUrl,
): Promise<void> {
  try {
    const url = await getDownloadUrl(partId, attachmentId)
    window.open(url, '_blank', 'noopener,noreferrer')
  } catch (error) {
    onError(error instanceof Error ? error.message : 'We couldn’t open this file.')
  }
}

/**
 * UID-528 follow-up — the design's "NAME & DRAWING" column (`dc.html:3571`)
 * shows exactly one chip: the part's DRAWING, never its model/step file.
 * `resolveRfqLineFiles` can resolve both an attached `model` and `drawing`
 * per part (UID-528's own resolution rule allows one chip per kind), but
 * this column only ever had room for the drawing in the design — rendering
 * both was never intentional, just an oversight once real files existed to
 * render. Filtering here (not at the resolver) keeps the resolver's
 * contract honest for any OTHER future consumer that might want the model
 * file too.
 */
export function drawingFilesOnly(
  files: readonly RfqLineFileView[] | undefined,
): readonly RfqLineFileView[] | undefined {
  return files?.filter((file) => file.kind === 'drawing')
}

/**
 * UID-267/UID-528 (ADR-0092, design L1763/L3571) — one per-part supporting-
 * file chip: a small file-kind glyph + the filename, rendered as a real
 * `<button>` (keyboard-reachable, real focus outline — never a bare
 * `<span onClick>`). `file.name` is free text rendered as plain JSX text
 * (React-escaped — the same ADR-0063 `requirements` posture), never
 * `dangerouslySetInnerHTML`.
 *
 * UID-528 — `line.files` now carries REAL Parts Library attachments
 * (`name`/`id` real, `id` the real attachment UUID); the demo no-op click
 * this used to be is gone. `file.id` is OPTIONAL on the wire
 * (`RfqLineFileView.id?`) — with no id there is nothing to download, so the
 * chip renders `disabled` (no `onClick`, removed from the tab order, still
 * carries its accessible name) rather than firing a request that can only
 * 404. `partId` is `line.sourceable.id` — every call site already has it.
 */
export function RfqLineFileChip({
  partId,
  file,
  onDownloadError,
}: {
  readonly partId: string
  readonly file: RfqLineFileView
  readonly onDownloadError: (message: string) => void
}) {
  const attachmentId = file.id
  return (
    <button
      type="button"
      className="rfq-line-file-chip"
      aria-label={`Open ${file.name}`}
      disabled={!attachmentId}
      onClick={
        attachmentId
          ? () => {
              void openRfqLineFile(partId, attachmentId, onDownloadError)
            }
          : undefined
      }
    >
      {file.kind === 'model' ? <FileModelGlyph /> : <Icon name="document" />}
      <span>{file.name}</span>
    </button>
  )
}

/**
 * UID-267 — the "model" (3D CAD) file-kind glyph (`drawing`/`other` reuse
 * the shared `Icon name="document"`, which already registers a document/
 * file-text glyph). No 3D/model glyph exists in the shared, closed
 * `IconName` union in `icon-registry.ts`, so rather than register a new
 * shared icon name this mirrors `RetryGlyph`'s inline-SVG precedent (same
 * `lucide-glyphs.tsx` stroke-icon convention — `viewBox 0 0 24 24`,
 * `stroke="currentColor"`, round caps/joins) so it drops in visually
 * identical to the other icons around it. Path data is the same box/cube
 * pictogram already vetted for this app's stroke-icon language (`BoxGlyph`
 * in `lucide-glyphs.tsx`) — reused here because a box is the natural
 * pictogram for a 3D model file, not a newly invented shape.
 */
function FileModelGlyph() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '1em',
        height: '1em',
        fontSize: '1em',
        lineHeight: 0,
        flexShrink: 0,
      }}
    >
      <svg
        width="1em"
        height="1em"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
        <path d="m3.3 7 8.7 5 8.7-5" />
        <path d="M12 22V12" />
      </svg>
    </span>
  )
}
